import { z } from 'zod';

export interface CalVerEntry {
  year: number;
  month: number;
  release: number;
  patch?: number;
  modifier?: string;
}

const matchGroups = z.object({
  year: z.coerce.number(),
  month: z.coerce.number(),
  release: z.coerce.number(),
  patch: z.coerce.number().optional(),
});

/**
 * Handles parsing and manipulating version strings.
 *
 * Examples:
 * - `2025.8.0`
 * - `2025.8.0.1`
 * - `2025.8.0.1-8db3ac`
 */
export class CalVer {
  private constructor(private readonly entry: CalVerEntry) {}

  public static fromString(str: string): CalVer | undefined {
    const match =
      /^(?<year>(19|20)\d{2})\.(?<month>[1-9]|1[012])\.(?<release>[0-9]+)(\.(?<patch>[0-9]+))?$/.exec(
        str,
      );
    if (!match) {
      return;
    }
    const safeParse = matchGroups.safeParse(match.groups);
    if (!safeParse.success) {
      return;
    }
    const { year, month, release, patch } = safeParse.data;
    return new CalVer({
      year,
      month,
      release,
      patch,
    });
  }

  public next(): CalVer {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    let release: number;
    if (year === this.entry.year && month === this.entry.month) {
      release = this.entry.release + 1;
    } else {
      release = 0;
    }
    return new CalVer({
      year,
      month,
      release,
    });
  }

  public patched(): CalVer {
    return new CalVer({
      ...this.entry,
      patch: this.entry.patch !== undefined ? this.entry.patch + 1 : 1,
    });
  }

  public withModifier(modifier: string): CalVer {
    return new CalVer({
      ...this.entry,
      modifier,
    });
  }

  public toString(): string {
    let base = `${this.entry.year}.${this.entry.month}.${this.entry.release}`;
    if (this.entry.patch !== undefined) {
      base += `.${this.entry.patch}`;
    }
    if (this.entry.modifier) {
      base += `-${this.entry.modifier}`;
    }
    return base;
  }
}
